// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable, signal, computed } from '@angular/core';
import { KeyProject, Workspace, ViewState, EmptyStateConfig } from '@lfx/key-projects/key-projects.model';
import { WORKSPACE_NAMES } from '@lfx/key-projects/key-projects.constants';

/**
 * State management service for Key Projects feature
 * Handles all reactive state using Angular signals
 * Single Responsibility: Manages application state only
 */
@Injectable({
  providedIn: 'root',
})
export class KeyProjectsStateService {
  // Private state signals
  private readonly _projects = signal<KeyProject[]>([]);
  private readonly _workspaces = signal<Workspace[]>([]);
  private readonly _viewState = signal<ViewState>({
    mode: 'workspace-projects',
  });
  private readonly _loading = signal<boolean>(false);
  private readonly _initialLoading = signal<boolean>(true);
  private readonly _initialError = signal<string | null>(null);
  private readonly _creatingDefaultWorkspaces = signal<boolean>(false);
  private readonly _loadingWorkspaceProjects = signal<boolean>(false);

  // Read-only accessors
  readonly projects = this._projects.asReadonly();
  readonly workspaces = this._workspaces.asReadonly();
  readonly viewState = this._viewState.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly initialLoading = this._initialLoading.asReadonly();
  readonly initialError = this._initialError.asReadonly();
  readonly creatingDefaultWorkspaces = this._creatingDefaultWorkspaces.asReadonly();
  readonly loadingWorkspaceProjects = this._loadingWorkspaceProjects.asReadonly();

  // Computed properties
  readonly currentProjects = computed((): KeyProject[] => {
    const state = this._viewState();

    if (state.mode === 'workspace-projects' && state.selectedWorkspaceId) {
      const workspace = this._workspaces().find(ws => ws.id === state.selectedWorkspaceId);
      if (!workspace) return [];

      return this._projects().filter(project => {
        if (!workspace.projectSlugs) return false;

        return workspace.projectSlugs.some(projectSlug => {
          const expectedFormat = `${project.id}:${project.slug}`;
          return projectSlug === expectedFormat || projectSlug === project.slug;
        });
      });
    }

    return [];
  });

  readonly emptyStateConfig = computed((): EmptyStateConfig => {
    const state = this._viewState();
    const workspaces = this._workspaces();
    const creatingDefaultWorkspaces = this._creatingDefaultWorkspaces();
    const initialLoading = this._initialLoading();

    // Initial loading state
    if (initialLoading && !creatingDefaultWorkspaces) {
      return {
        title: 'Loading your key projects',
        message: 'Please wait while we load your key projects and workspaces...',
        actionText: '',
        showAction: false,
        iconSrc: 'assets/img/svgs/loading.svg',
        buttonIcon: '',
      };
    }

    // Creating default workspace with most active projects
    if (creatingDefaultWorkspaces) {
      return {
        title: 'Please wait while we create your first workspace',
        message: `Workspaces help you organize projects by topic, industry, or tech stack.</br>We are loading a "${WORKSPACE_NAMES.MOST_ACTIVE}" workspace that you can freely edit or delete and create new ones.`,
        actionText: '',
        showAction: false,
        iconSrc: 'assets/img/svgs/loading.svg',
        buttonIcon: '',
      };
    }

    // No workspaces created yet
    if (workspaces.length === 0) {
      return {
        title: 'Create your first workspace of key projects',
        message: 'Workspaces help you organize projects by topic, industry, or tech stack',
        actionText: 'Add workspace',
        showAction: true,
        iconSrc: 'assets/img/svgs/folder-empty.svg',
        buttonIcon: 'far fa-folder-plus',
      };
    }

    // Workspace selected but no projects in it
    if (state.mode === 'workspace-projects') {
      return {
        title: 'No projects in this workspace yet',
        message: 'Select the key projects you want to track',
        actionText: 'Add project',
        showAction: true,
        iconSrc: 'assets/img/svgs/layer-group.svg',
        buttonIcon: 'fas fa-plus',
      };
    }

    return {
      title: 'No data available',
      message: 'There is no data to display at this time.',
      showAction: false,
    };
  });

  // State mutation methods
  setProjects(projects: KeyProject[]): void {
    this._projects.set(projects);
  }

  updateProjects(updateFn: (projects: KeyProject[]) => KeyProject[]): void {
    this._projects.update(updateFn);
  }

  setWorkspaces(workspaces: Workspace[]): void {
    this._workspaces.set(workspaces);
  }

  updateWorkspaces(updateFn: (workspaces: Workspace[]) => Workspace[]): void {
    this._workspaces.update(updateFn);
  }

  setViewState(viewState: ViewState): void {
    this._viewState.set(viewState);
  }

  setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  setInitialLoading(loading: boolean): void {
    this._initialLoading.set(loading);
  }

  setInitialError(error: string | null): void {
    this._initialError.set(error);
  }

  setCreatingDefaultWorkspaces(creating: boolean): void {
    this._creatingDefaultWorkspaces.set(creating);
  }

  setLoadingWorkspaceProjects(loading: boolean): void {
    this._loadingWorkspaceProjects.set(loading);
  }

  // Query methods
  getProjectById(id: string): KeyProject | undefined {
    return this._projects().find(project => project.id === id);
  }

  getWorkspaceById(id: string): Workspace | undefined {
    return this._workspaces().find(workspace => workspace.id === id);
  }

  getProjectsForWorkspace(workspaceId: string): KeyProject[] {
    const workspace = this.getWorkspaceById(workspaceId);
    if (!workspace) {
      return [];
    }
    return this._projects().filter(project => {
      if (!workspace.projectSlugs) return false;

      return workspace.projectSlugs.some(projectSlug => {
        const expectedFormat = `${project.id}:${project.slug}`;
        return projectSlug === expectedFormat || projectSlug === project.slug;
      });
    });
  }

  /**
   * Find the default workspace (Most Active Projects)
   */
  findDefaultWorkspace(): Workspace | undefined {
    return this._workspaces().find(ws => ws.name === WORKSPACE_NAMES.MOST_ACTIVE);
  }

  /**
   * @deprecated Use findDefaultWorkspace instead
   */
  findMostActiveWorkspace(): Workspace | undefined {
    return this.findDefaultWorkspace();
  }

  addWorkspace(workspace: Workspace): void {
    this.updateWorkspaces(workspaces => [...workspaces, workspace]);
  }

  addWorkspaces(newWorkspaces: Workspace[]): void {
    this.updateWorkspaces(workspaces => [...workspaces, ...newWorkspaces]);
  }

  removeWorkspace(workspaceId: string): void {
    this.updateWorkspaces(workspaces => workspaces.filter(ws => ws.id !== workspaceId));
  }

  selectWorkspace(workspaceId: string): void {
    const workspace = this.getWorkspaceById(workspaceId);
    this.setViewState({
      mode: 'workspace-projects',
      selectedWorkspaceId: workspaceId,
      selectedWorkspace: workspace,
    });
  }

  selectFirstWorkspace(): void {
    const workspaces = this._workspaces();
    if (workspaces.length > 0) {
      this.selectWorkspace(workspaces[0].id);
    }
  }
}

